import { useState, useRef, useEffect } from 'react';
import api from '@/lib/api';
import { Download, Upload, FileText, Users, RefreshCw, AlertTriangle, CheckSquare, Square } from 'lucide-react';
import TreeList from './ReportingTreeNode';

/**
 * ReportingTree — interactive, drag-and-drop org chart.
 * Props:
 *   divisionId: string (scope)
 *   rootPersonId: string (optional — show only this person's sub-tree)
 *   canEdit: boolean (default false)
 *   onPersonClick: fn(node)
 */
export default function ReportingTree({ divisionId, rootPersonId, canEdit = false, onPersonClick }) {
  const [tree, setTree] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({});
  const [dragNode, setDragNode] = useState(null);
  const [dropTarget, setDropTarget] = useState(null);
  const [pendingMove, setPendingMove] = useState(null);
  const [bulkMode, setBulkMode] = useState(false);
  const [bulkSelected, setBulkSelected] = useState({});
  const [bulkTarget, setBulkTarget] = useState(null);
  const [saving, setSaving] = useState(false);
  const fileInputRef = useRef(null);

  const expandAllDown = (node, map, depth) => {
    if (depth < 2) map[node.id] = true;
    (node.children || []).forEach(c => expandAllDown(c, map, depth + 1));
  };

  const load = async () => {
    setLoading(true);
    const params = new URLSearchParams();
    if (divisionId) params.append('division_id', divisionId);
    if (rootPersonId) params.append('root_person_id', rootPersonId);
    try {
      const { data } = await api.get('/people/org-tree?' + params.toString());
      setTree(data);
      const map = {};
      (data.roots || []).forEach(r => expandAllDown(r, map, 0));
      setExpanded(map);
    } catch (e) {
      console.error('org-tree load failed', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [divisionId, rootPersonId]);

  const toggle = (id) => setExpanded(prev => ({ ...prev, [id]: !prev[id] }));

  const hasDescendant = (node, targetId) => {
    if (!node) return false;
    if (node.id === targetId) return true;
    return (node.children || []).some(c => hasDescendant(c, targetId));
  };

  const handleDragStart = (e, node) => {
    if (!canEdit) return;
    // In bulk mode, if the dragged node is selected, drag all selected nodes
    if (bulkMode && bulkSelected[node.id]) {
      setDragNode({ ...node, __bulk: true, __selected: Object.keys(bulkSelected).filter(k => bulkSelected[k]) });
    } else {
      setDragNode(node);
    }
    try { e.dataTransfer.setData('text/plain', node.id); } catch (err) { /* ignore */ }
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e, node) => {
    if (!canEdit || !dragNode) return;
    if (dragNode.id === node.id) return;
    if (hasDescendant(dragNode, node.id)) return;
    e.preventDefault();
    setDropTarget(node.id);
  };

  const handleDragLeave = () => setDropTarget(null);

  const handleDrop = (e, node) => {
    if (!canEdit || !dragNode) return;
    e.preventDefault();
    setDropTarget(null);
    if (dragNode.id === node.id) return;
    if (hasDescendant(dragNode, node.id)) return;
    if (dragNode.__bulk && dragNode.__selected && dragNode.__selected.length > 0) {
      setBulkTarget({ newParent: node, selectedIds: dragNode.__selected });
    } else {
      setPendingMove({ person: dragNode, newParent: node, alsoOutlet: dragNode.outlet !== node.outlet });
    }
    setDragNode(null);
  };

  const toggleSelect = (nodeId) => {
    setBulkSelected(prev => ({ ...prev, [nodeId]: !prev[nodeId] }));
  };

  const confirmBulkMove = async (alsoOutlet) => {
    if (!bulkTarget) return;
    setSaving(true);
    try {
      // Flatten tree to find full node data for each selected
      const findNode = (nodes, id) => {
        for (const n of nodes) {
          if (n.id === id) return n;
          const c = findNode(n.children || [], id);
          if (c) return c;
        }
        return null;
      };
      const changes = bulkTarget.selectedIds
        .map(id => {
          const node = findNode(tree.roots, id);
          if (!node) return null;
          // Prevent moving a parent onto one of its own descendants
          if (hasDescendant(node, bulkTarget.newParent.id)) return null;
          const ch = { person_id: id, reports_to: bulkTarget.newParent.name };
          if (alsoOutlet) ch.outlet = bulkTarget.newParent.outlet;
          return ch;
        })
        .filter(Boolean);
      await api.post('/people/bulk-reassign', { changes });
      setBulkTarget(null);
      setBulkSelected({});
      setBulkMode(false);
      await load();
    } catch (e) {
      alert('Bulk move failed: ' + (e.response && e.response.data ? e.response.data.detail : e.message));
    } finally {
      setSaving(false);
    }
  };

  const confirmMove = async () => {
    if (!pendingMove) return;
    setSaving(true);
    try {
      const payload = { person_id: pendingMove.person.id, reports_to: pendingMove.newParent.name };
      if (pendingMove.alsoOutlet) payload.outlet = pendingMove.newParent.outlet;
      await api.post('/people/bulk-reassign', { changes: [payload] });
      setPendingMove(null);
      await load();
    } catch (e) {
      alert('Move failed: ' + (e.response && e.response.data ? e.response.data.detail : e.message));
    } finally {
      setSaving(false);
    }
  };

  const BACKEND = process.env.REACT_APP_BACKEND_URL;

  const exportCSV = async () => {
    const token = localStorage.getItem('access_token');
    const url = BACKEND + '/api/people/org-tree/export-csv' + (divisionId ? '?division_id=' + divisionId : '');
    try {
      const r = await fetch(url, { headers: { Authorization: 'Bearer ' + token } });
      const blob = await r.blob();
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = 'org_tree_' + (divisionId || 'all') + '.csv';
      a.click();
    } catch { alert('Export failed'); }
  };

  const downloadTemplate = async () => {
    const token = localStorage.getItem('access_token');
    try {
      const r = await fetch(BACKEND + '/api/people/org-tree/csv-template', { headers: { Authorization: 'Bearer ' + token } });
      const blob = await r.blob();
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = 'org_tree_template.csv';
      a.click();
    } catch { alert('Template download failed'); }
  };

  const importCSV = async (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    const fd = new FormData();
    fd.append('file', file);
    try {
      const { data } = await api.post('/people/org-tree/import-csv', fd);
      alert('Import complete: ' + data.updated + ' updated, ' + data.created + ' created' + (data.errors && data.errors.length ? (', ' + data.errors.length + ' errors') : ''));
      await load();
    } catch (err) {
      alert('Import failed: ' + (err.response && err.response.data ? err.response.data.detail : err.message));
    } finally {
      e.target.value = '';
    }
  };

  if (loading) return <p className="py-8 text-center text-sm" style={{ color: '#9CA3AF' }}>Loading reporting tree...</p>;
  if (!tree || !tree.roots || tree.roots.length === 0) return <p className="py-8 text-center text-sm" style={{ color: '#9CA3AF' }}>No people in this scope.</p>;

  return (
    <div className="space-y-3" data-testid="reporting-tree">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Users size={14} style={{ color: '#1B2A4A' }} />
          <p className="text-xs font-semibold" style={{ color: '#1B2A4A' }}>
            Reporting Tree — {tree.total_people} people · {tree.roots.length} roots
          </p>
        </div>
        <div className="flex items-center gap-1.5">
          <button onClick={load} className="flex items-center gap-1 text-[11px] px-2 py-1 rounded border hover:bg-gray-50" style={{ borderColor: '#E5E1DC', color: '#6B7280' }} data-testid="reftree-refresh">
            <RefreshCw size={11} /> Refresh
          </button>
          <button onClick={downloadTemplate} className="flex items-center gap-1 text-[11px] px-2 py-1 rounded border hover:bg-gray-50" style={{ borderColor: '#E5E1DC', color: '#6B7280' }} data-testid="reftree-template">
            <FileText size={11} /> Template
          </button>
          <button onClick={exportCSV} className="flex items-center gap-1 text-[11px] px-2 py-1 rounded border hover:bg-gray-50" style={{ borderColor: '#E5E1DC', color: '#6B7280' }} data-testid="reftree-export">
            <Download size={11} /> Export CSV
          </button>
          {canEdit ? (
            <>
              <button onClick={() => { setBulkMode(!bulkMode); setBulkSelected({}); }}
                className="flex items-center gap-1 text-[11px] px-2 py-1 rounded border hover:bg-gray-50"
                style={{ borderColor: bulkMode ? '#1B2A4A' : '#E5E1DC', color: bulkMode ? '#1B2A4A' : '#6B7280', backgroundColor: bulkMode ? '#EFF6FF' : '#fff' }}
                data-testid="reftree-bulk-toggle">
                {bulkMode ? <CheckSquare size={11} /> : <Square size={11} />}
                {bulkMode ? `Bulk (${Object.values(bulkSelected).filter(Boolean).length})` : 'Bulk edit'}
              </button>
              <button onClick={() => fileInputRef.current && fileInputRef.current.click()} className="flex items-center gap-1 text-[11px] px-2 py-1 rounded border hover:bg-gray-50" style={{ borderColor: '#E5E1DC', color: '#6B7280' }} data-testid="reftree-import">
                <Upload size={11} /> Import CSV
              </button>
              <input ref={fileInputRef} type="file" accept=".csv" className="hidden" onChange={importCSV} />
            </>
          ) : null}
        </div>
      </div>

      {canEdit ? (
        <div className="flex items-center gap-2 p-2 rounded-md text-[11px]" style={{ backgroundColor: bulkMode ? '#EFF6FF' : '#F0FDF4', color: bulkMode ? '#185FA5' : '#065F46' }}>
          <span>Tip:</span>
          <span>
            {bulkMode
              ? 'Click to select multiple people, then drag any selected person to a new manager. All selected people will move together.'
              : 'Drag any person onto a new manager to re-parent. A confirmation dialog will show the changes before saving.'}
          </span>
        </div>
      ) : null}

      <div className="bg-white rounded-xl border p-3 overflow-x-auto" style={{ borderColor: '#E5E1DC' }}>
        <TreeList
          roots={tree.roots}
          expanded={expanded}
          toggle={toggle}
          canEdit={canEdit}
          dragNode={dragNode}
          dropTarget={dropTarget}
          onDragStart={handleDragStart}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onPersonClick={bulkMode ? (n) => toggleSelect(n.id) : onPersonClick}
          bulkMode={bulkMode}
          bulkSelected={bulkSelected}
        />
      </div>

      {pendingMove ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="bg-white rounded-xl p-5 max-w-md w-full space-y-3" data-testid="reftree-confirm">
            <div className="flex items-center gap-2">
              <AlertTriangle size={18} style={{ color: '#D97706' }} />
              <h3 className="text-base font-semibold" style={{ color: '#1B2A4A' }}>Confirm reassignment</h3>
            </div>
            <div className="text-sm space-y-2" style={{ color: '#374151' }}>
              <p><strong>{pendingMove.person.name}</strong> ({pendingMove.person.designation || pendingMove.person.role})</p>
              <div className="bg-[#F7F5F2] rounded-md p-3 space-y-1">
                <p className="text-xs">
                  <span style={{ color: '#9CA3AF' }}>Reports to: </span>
                  <span className="line-through" style={{ color: '#D4726A' }}>{pendingMove.person.reports_to || '—'}</span>
                  {' → '}
                  <span className="font-semibold" style={{ color: '#2D8B5E' }}>{pendingMove.newParent.name}</span>
                </p>
                {pendingMove.alsoOutlet ? (
                  <label className="text-xs flex items-center gap-1.5 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={pendingMove.alsoOutlet}
                      onChange={(e) => setPendingMove(prev => ({ ...prev, alsoOutlet: e.target.checked }))}
                      data-testid="reftree-also-outlet"
                    />
                    <span>Also move outlet: </span>
                    <span className="line-through" style={{ color: '#D4726A' }}>{pendingMove.person.outlet || '—'}</span>
                    {' → '}
                    <span className="font-semibold" style={{ color: '#2D8B5E' }}>{pendingMove.newParent.outlet || '—'}</span>
                  </label>
                ) : null}
              </div>
            </div>
            <div className="flex items-center justify-end gap-2">
              <button onClick={() => setPendingMove(null)} className="px-3 py-1.5 text-sm rounded border" style={{ borderColor: '#E5E1DC', color: '#6B7280' }} data-testid="reftree-cancel">
                Cancel
              </button>
              <button onClick={confirmMove} disabled={saving} className="px-3 py-1.5 text-sm rounded text-white font-medium" style={{ backgroundColor: '#1B2A4A', opacity: saving ? 0.6 : 1 }} data-testid="reftree-confirm-btn">
                {saving ? 'Saving...' : 'Confirm move'}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {bulkTarget ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="bg-white rounded-xl p-5 max-w-md w-full space-y-3" data-testid="reftree-bulk-confirm">
            <div className="flex items-center gap-2">
              <AlertTriangle size={18} style={{ color: '#185FA5' }} />
              <h3 className="text-base font-semibold" style={{ color: '#1B2A4A' }}>Confirm bulk reassignment</h3>
            </div>
            <div className="text-sm space-y-2" style={{ color: '#374151' }}>
              <p><strong>{bulkTarget.selectedIds.length} people</strong> will report to <strong style={{ color: '#2D8B5E' }}>{bulkTarget.newParent.name}</strong> ({bulkTarget.newParent.designation})</p>
              <div className="bg-[#F7F5F2] rounded-md p-2 max-h-40 overflow-y-auto text-xs space-y-0.5">
                {bulkTarget.selectedIds.map(id => {
                  const findNode = (nodes, tid) => { for (const n of nodes) { if (n.id === tid) return n; const c = findNode(n.children || [], tid); if (c) return c; } return null; };
                  const node = findNode(tree.roots, id);
                  return node ? <p key={id} style={{ color: '#6B7280' }}>• {node.name} <span style={{ color: '#9CA3AF' }}>({node.reports_to || '—'} → {bulkTarget.newParent.name})</span></p> : null;
                })}
              </div>
            </div>
            <div className="flex items-center justify-end gap-2">
              <button onClick={() => setBulkTarget(null)} className="px-3 py-1.5 text-sm rounded border" style={{ borderColor: '#E5E1DC', color: '#6B7280' }} data-testid="reftree-bulk-cancel">Cancel</button>
              <button onClick={() => confirmBulkMove(false)} disabled={saving} className="px-3 py-1.5 text-sm rounded border" style={{ borderColor: '#1B2A4A', color: '#1B2A4A', opacity: saving ? 0.6 : 1 }} data-testid="reftree-bulk-reports-only">
                Reports only
              </button>
              <button onClick={() => confirmBulkMove(true)} disabled={saving} className="px-3 py-1.5 text-sm rounded text-white font-medium" style={{ backgroundColor: '#1B2A4A', opacity: saving ? 0.6 : 1 }} data-testid="reftree-bulk-confirm-btn">
                {saving ? 'Saving...' : `Reports + Outlet (${bulkTarget.newParent.outlet || '—'})`}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
